/**
 * Custom settings menu (gear → Quality / Playback speed → submenus).
 *
 * Replaces videojs-contrib-quality-menu's button with a two-level panel like
 * modern players: a main view listing "Quality" and "Playback speed" rows with
 * their current values, and submenus to change them. Quality options come from
 * videojs-contrib-quality-levels; non-ABR streams show a single "Default"
 * entry. All markup is built from static strings + textContent — no user data
 * flows through innerHTML.
 */

import type Player from 'video.js/dist/types/player'
import { submitPlayerFeedback } from './api'
import { openShareOverlay } from './share-overlay'

interface QualityLevel {
  height?: number
  bitrate?: number
  enabled: boolean
}

interface QualityLevelList {
  length: number
  selectedIndex: number
  [index: number]: QualityLevel
  on(event: string, cb: () => void): void
  off(event: string, cb: () => void): void
}

const SVG_ATTRS =
  'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"'

const ICON_GEAR =
  `<svg ${SVG_ATTRS}><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>`
const ICON_RIGHT = `<svg ${SVG_ATTRS}><polyline points="9 18 15 12 9 6"/></svg>`
const ICON_LEFT = `<svg ${SVG_ATTRS}><polyline points="15 18 9 12 15 6"/></svg>`
const ICON_CHECK = `<svg ${SVG_ATTRS}><polyline points="20 6 9 17 4 12"/></svg>`

/**
 * Capped at 1.5x. These are live streams: the player can only run ahead of the
 * broadcast until it reaches the live edge, at which point it stalls waiting
 * for segments that do not exist yet. 2x reached that wall almost immediately
 * and read as a broken player rather than a fast one.
 */
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5]

const FEEDBACK_REASONS = [
  "Video doesn't play",
  'Video is poor quality',
  'Buffering',
  'Audio not working',
  'Poor audio quality',
  'Seeing a black screen',
  "Can't fast-forward or rewind",
  'Something else',
]

const REPORT_REASONS = [
  'Sexual content',
  'Violent or repulsive content',
  'Hateful or abusive content',
  'Harassment or bullying',
  'Harmful or dangerous acts',
  'Misinformation',
  'Child abuse',
  'Promotes terrorism',
  'Spam or misleading',
]

type View = 'main' | 'quality' | 'speed' | 'feedback' | 'report' | 'thanks'

export interface SettingsMenuHandle {
  /** Open the panel directly at a given view (used by the context menu) */
  openAt(view: 'main' | 'feedback' | 'report'): void
  dispose(): void
}

export interface SettingsMenuContext {
  embedId: string
  sessionId: string | null
  /** Shareable watch URL + display title for the share overlay */
  shareUrl: string
  title: string
}

export function mountSettingsMenu(player: Player, ctx: SettingsMenuContext): SettingsMenuHandle {
  const playerEl = player.el() as HTMLElement
  const controlBar = playerEl.querySelector('.vjs-control-bar')
  if (!controlBar) return { openAt: () => undefined, dispose: () => undefined }

  // ── gear button in the control bar ────────────────────────────────────
  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'vjs-control vjs-button lp-settings-button'
  button.setAttribute('aria-label', 'Settings')
  button.innerHTML = ICON_GEAR // static markup

  const fullscreen = controlBar.querySelector('.vjs-fullscreen-control')
  controlBar.insertBefore(button, fullscreen)

  // ── panel ─────────────────────────────────────────────────────────────
  const panel = document.createElement('div')
  panel.className = 'lp-settings-panel'
  const viewEl = document.createElement('div')
  viewEl.className = 'lp-settings-view'
  panel.appendChild(viewEl)
  playerEl.appendChild(panel)

  let open = false
  let view: View = 'main'
  let heightResetTimer: ReturnType<typeof setTimeout> | undefined

  const qualityLevels = (
    player as unknown as { qualityLevels?: () => QualityLevelList }
  ).qualityLevels?.()

  const qualityLabelFor = (level: QualityLevel | undefined): string => {
    if (!level) return 'Default'
    if (level.height) return `${level.height}p`
    if (level.bitrate) return `${Math.round(level.bitrate / 1000)} kbps`
    return 'Default'
  }

  /** true when no specific level is forced (all enabled) */
  const isAuto = (): boolean => {
    if (!qualityLevels || qualityLevels.length <= 1) return true
    for (let i = 0; i < qualityLevels.length; i++) {
      if (!qualityLevels[i].enabled) return false
    }
    return true
  }

  const currentQualityLabel = (): string => {
    if (!qualityLevels || qualityLevels.length === 0) return 'Default'
    const selected = qualityLevels[qualityLevels.selectedIndex]
    if (qualityLevels.length === 1) return qualityLabelFor(selected)
    return isAuto() ? `Automatic ${qualityLabelFor(selected)}` : qualityLabelFor(selected)
  }

  const selectQuality = (index: number): void => {
    if (!qualityLevels) return
    for (let i = 0; i < qualityLevels.length; i++) {
      qualityLevels[i].enabled = index === -1 || i === index
    }
  }

  const currentSpeed = (): number => player.playbackRate() ?? 1

  // ── rendering (all text via textContent) ──────────────────────────────

  const makeIcon = (svg: string, cls: string): HTMLElement => {
    const span = document.createElement('span')
    span.className = cls
    span.innerHTML = svg // static markup
    return span
  }

  const makeRow = (label: string, value: string, onClick: () => void): HTMLElement => {
    const row = document.createElement('button')
    row.type = 'button'
    row.className = 'lp-settings-row'
    const l = document.createElement('span')
    l.className = 'lp-settings-label'
    l.textContent = label
    const r = document.createElement('span')
    r.className = 'lp-settings-value'
    if (value) {
      const v = document.createElement('span')
      v.textContent = value
      r.appendChild(v)
    }
    r.appendChild(makeIcon(ICON_RIGHT, 'lp-settings-chevron'))
    row.append(l, r)
    row.addEventListener('click', onClick)
    return row
  }

  /** Switch views with a slide (+ panel height morph). */
  const switchView = (next: View, direction: 'fwd' | 'back'): void => {
    const prevHeight = panel.offsetHeight
    view = next
    render()

    viewEl.classList.remove('lp-slide-fwd', 'lp-slide-back')
    void viewEl.offsetWidth // restart the animation
    viewEl.classList.add(direction === 'fwd' ? 'lp-slide-fwd' : 'lp-slide-back')

    // Height morph: lock old height, transition to the new content height
    if (heightResetTimer) clearTimeout(heightResetTimer)
    panel.classList.add('lp-animating')
    panel.style.height = `${prevHeight}px`
    requestAnimationFrame(() => {
      panel.style.height = `${viewEl.offsetHeight + 16}px` // + panel padding
    })
    heightResetTimer = setTimeout(() => {
      panel.style.height = ''
      panel.classList.remove('lp-animating')
    }, 280)
  }

  const makeHeader = (title: string): HTMLElement => {
    const header = document.createElement('button')
    header.type = 'button'
    header.className = 'lp-settings-header'
    header.append(makeIcon(ICON_LEFT, 'lp-settings-chevron'), document.createTextNode(title))
    header.addEventListener('click', () => switchView('main', 'back'))
    return header
  }

  const makeOption = (label: string, selected: boolean, onClick: () => void, autoBack = true): HTMLElement => {
    const opt = document.createElement('button')
    opt.type = 'button'
    opt.className = 'lp-settings-option' + (selected ? ' selected' : '')
    const check = makeIcon(ICON_CHECK, 'lp-settings-check')
    const l = document.createElement('span')
    l.textContent = label
    opt.append(check, l)
    opt.addEventListener('click', () => {
      onClick()
      if (autoBack) switchView('main', 'back')
    })
    return opt
  }

  let thanksTimer: ReturnType<typeof setTimeout> | undefined

  const submitAndThank = (kind: 'feedback' | 'report', reason: string): void => {
    void submitPlayerFeedback({ kind, reason, embedId: ctx.embedId, sessionId: ctx.sessionId })
    switchView('thanks', 'fwd')
    if (thanksTimer) clearTimeout(thanksTimer)
    thanksTimer = setTimeout(() => setOpen(false), 1800)
  }

  const render = (): void => {
    viewEl.textContent = ''

    if (view === 'main') {
      viewEl.appendChild(makeRow('Quality', currentQualityLabel(), () => switchView('quality', 'fwd')))
      viewEl.appendChild(
        makeRow('Playback speed', currentSpeed() === 1 ? 'Normal' : `${currentSpeed()}x`, () =>
          switchView('speed', 'fwd'),
        ),
      )
      viewEl.appendChild(
        makeRow('Share', '', () => {
          setOpen(false)
          openShareOverlay(playerEl, { url: ctx.shareUrl, title: ctx.title })
        }),
      )
      viewEl.appendChild(makeRow('Report', '', () => switchView('report', 'fwd')))
      viewEl.appendChild(makeRow('Send feedback', '', () => switchView('feedback', 'fwd')))
      return
    }

    if (view === 'quality') {
      viewEl.appendChild(makeHeader('Quality'))
      if (!qualityLevels || qualityLevels.length <= 1) {
        // Non-ABR stream: single default quality, shown as-is
        viewEl.appendChild(
          makeOption(
            qualityLevels && qualityLevels.length === 1
              ? qualityLabelFor(qualityLevels[0])
              : 'Default',
            true,
            () => undefined,
          ),
        )
        return
      }
      viewEl.appendChild(makeOption('Automatic', isAuto(), () => selectQuality(-1)))
      const indices = Array.from({ length: qualityLevels.length }, (_, i) => i).sort(
        (a, b) => (qualityLevels[b].height ?? 0) - (qualityLevels[a].height ?? 0),
      )
      for (const i of indices) {
        viewEl.appendChild(
          makeOption(
            qualityLabelFor(qualityLevels[i]),
            !isAuto() && qualityLevels[i].enabled,
            () => selectQuality(i),
          ),
        )
      }
      return
    }

    if (view === 'feedback' || view === 'report') {
      const isReport = view === 'report'
      viewEl.appendChild(makeHeader(isReport ? 'Report' : 'Send feedback'))
      const subtitle = document.createElement('div')
      subtitle.className = 'lp-settings-subtitle'
      subtitle.textContent = isReport ? "What's wrong with this content?" : 'Video playback'
      viewEl.appendChild(subtitle)
      for (const reason of isReport ? REPORT_REASONS : FEEDBACK_REASONS) {
        viewEl.appendChild(
          makeOption(reason, false, () => submitAndThank(isReport ? 'report' : 'feedback', reason), false),
        )
      }
      return
    }

    if (view === 'thanks') {
      const box = document.createElement('div')
      box.className = 'lp-settings-thanks'
      const icon = makeIcon(ICON_CHECK, 'lp-settings-thanks-icon')
      const title = document.createElement('strong')
      title.textContent = 'Thanks!'
      const sub = document.createElement('span')
      sub.textContent = 'Your input helps us improve the stream.'
      box.append(icon, title, sub)
      viewEl.appendChild(box)
      return
    }

    // speed
    viewEl.appendChild(makeHeader('Playback speed'))
    for (const rate of SPEEDS) {
      viewEl.appendChild(
        makeOption(rate === 1 ? 'Normal' : `${rate}x`, currentSpeed() === rate, () =>
          player.playbackRate(rate),
        ),
      )
    }
  }

  // ── open/close ────────────────────────────────────────────────────────

  const setOpen = (next: boolean): void => {
    open = next
    if (!next && thanksTimer) clearTimeout(thanksTimer)
    if (open) {
      view = 'main'
      render()
    }
    panel.classList.toggle('open', open)
    button.classList.toggle('active', open)
  }

  button.addEventListener('click', (ev) => {
    ev.stopPropagation()
    setOpen(!open)
  })

  const onDocClick = (ev: MouseEvent): void => {
    if (!open) return
    const target = ev.target as Node
    if (!panel.contains(target) && !button.contains(target)) setOpen(false)
  }
  document.addEventListener('click', onDocClick, true)

  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape' && open) setOpen(false)
  }
  document.addEventListener('keydown', onKey, true)

  // Keep the main view's labels fresh while open
  const onLevelsChange = (): void => {
    if (open) render()
  }
  qualityLevels?.on('change', onLevelsChange)
  qualityLevels?.on('addqualitylevel', onLevelsChange)
  player.on('ratechange', onLevelsChange)

  return {
    openAt: (target) => {
      setOpen(true)
      if (target !== 'main') switchView(target, 'fwd')
    },
    dispose: () => {
      if (thanksTimer) clearTimeout(thanksTimer)
      document.removeEventListener('click', onDocClick, true)
      document.removeEventListener('keydown', onKey, true)
      qualityLevels?.off('change', onLevelsChange)
      qualityLevels?.off('addqualitylevel', onLevelsChange)
      button.remove()
      panel.remove()
    },
  }
}
